/**
 * SensitiveConsentBlock — публичный рендер блока «Явное согласие на
 * чувствительные данные» (WEB-2158 + WEB-2162 п.3).
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ КОМПОНЕНТ, а не ветка внутри `PublicBlockRenderer`. У блока
 * ДВА независимых значения формы, и второе живёт под служебным ключом
 * (`sensitiveShareValueKey`). Подписка на него — второй вызов
 * `useBlockFormValue`, а хук нельзя звать внутри `switch`: вынеси его наверх
 * общего рендерера — и КАЖДЫЙ блок воронки получил бы лишнюю подписку ради
 * одного редкого вида (ровно тот перерасход, который чинила WEB-1993 п.3).
 *
 * ДВЕ ГАЛОЧКИ НЕЗАВИСИМЫ. Закон штата Вашингтон требует раздельное согласие на
 * СБОР и на ПЕРЕДАЧУ третьим лицам: посетитель вправе дать первое и не дать
 * второе, поэтому вторая галочка не «подчинена» первой ни в разметке, ни в
 * значениях.
 *
 * БЕЗ ГАЛОЧКИ ПУСКАЕМ ДАЛЬШЕ. Об этом прямо сказано под галочками: стена перед
 * прохождением убивает конверсию, а правовой эффект даёт несохранение ответа
 * (замок стоит в `collectScreenAnswers`). Молчаливое «ничего не произошло»
 * было бы обманом посетителя — поэтому строка видимая, а не комментарий в коде.
 */
import React from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import { useBlockFormValue } from './FormValuesContext';
import { resolveOptionAccent } from './optionAccentColor';
import {
  sensitiveShareEnabled,
  sensitiveShareValueKey,
} from '@/components/quiz-runtime/sensitiveConsent';

export interface SensitiveConsentBlockProps {
  block: EditorBlock;
  /** Цвет текста блока — считает общий рендерер (лестница слоёв WEB-1702). */
  primaryColor: string;
  /** Подпись первой галочки, уже собранная общим рендерером (rich-текст). */
  collectLabel: React.ReactNode;
  /** Подпись второй галочки. */
  shareLabel: React.ReactNode;
  /** Строка плеера «пройти дальше можно» на языке воронки. */
  note: string;
}

/**
 * Умолчание кегля текста согласия. Юридическая фраза набирается мельче
 * основного текста страницы — тем же числом, что «Галочка согласия»
 * (terms_checkbox). Число названо ЗДЕСЬ, потому что подсказка панели обязана
 * называть фактическое умолчание рендера (FONT_SIZE_FIXED_DEFAULT, правило
 * WEB-1692 юнит 7), а не общие 16.
 */
export const CONSENT_TEXT_FONT_SIZE = 14;

const ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 8,
  cursor: 'pointer',
};

export function SensitiveConsentBlock({
  block,
  primaryColor,
  collectLabel,
  shareLabel,
  note,
}: SensitiveConsentBlockProps): React.ReactElement {
  const { value: collectValue, setValue: setCollectValue } = useBlockFormValue(
    block.id,
  );
  const shareKey = sensitiveShareValueKey(block.id);
  const { value: shareValue, setValue: setShareValue } =
    useBlockFormValue(shareKey);
  const accent = resolveOptionAccent(block.optionAccentColor);
  const textStyle = { fontSize: block.fontSize ?? CONSENT_TEXT_FONT_SIZE };
  const boxStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    accentColor: accent,
  };

  return (
    <div style={{ marginBottom: 16, color: primaryColor }}>
      <label style={ROW_STYLE} data-testid="sensitive-consent-collect">
        <input
          type="checkbox"
          name={block.id}
          checked={collectValue === 'true'}
          onChange={(e) =>
            setCollectValue(block.id, e.target.checked ? 'true' : 'false')
          }
          style={boxStyle}
        />
        <span style={textStyle}>{collectLabel}</span>
      </label>
      {sensitiveShareEnabled(block) && (
        <label style={ROW_STYLE} data-testid="sensitive-consent-share">
          <input
            type="checkbox"
            name={shareKey}
            checked={shareValue === 'true'}
            onChange={(e) =>
              setShareValue(shareKey, e.target.checked ? 'true' : 'false')
            }
            style={boxStyle}
          />
          <span style={textStyle}>{shareLabel}</span>
        </label>
      )}
      <div
        data-testid="sensitive-consent-note"
        style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
      >
        {note}
      </div>
    </div>
  );
}
